import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ---------------------------------------------------------------------------
// Workstream state types
// ---------------------------------------------------------------------------

/**
 * A swim-lane state for a workstream.  Workstreams carry an ordered list of
 * these states that represent columns (or lanes) stories can be placed in.
 */
export interface WorkstreamState {
  /** Stable unique identifier for this state within a workstream. */
  id: string;
  /** Display label shown as the swim-lane header. */
  title: string;
  /** Hex colour used to tint or badge this state in the UI. */
  color: string;
}

/**
 * The default ordered swim-lane states applied to every new workstream.
 * Colors mirror the "In Progress" child-status palette used for epics and stories.
 */
export const DEFAULT_WORKSTREAM_STATES: WorkstreamState[] = [
  { id: 'workstream-state-not-started', title: 'Not Started', color: '#9E9E9E' },
  { id: 'workstream-state-blocked',     title: 'Blocked',     color: '#FF5722' },
  { id: 'workstream-state-in-progress', title: 'In Progress', color: '#2196F3' },
  { id: 'workstream-state-completed',   title: 'Completed',   color: '#4CAF50' },
];

// ---------------------------------------------------------------------------
// Workstream structure types
// ---------------------------------------------------------------------------

/**
 * Links one Sprint to the pod(s) working it within a specific Workstream.
 *
 * This is the join record that supports:
 *   - Multiple pods assigned to a single sprint on the same workstream.
 *   - The same sprint dates running independently per workstream (e.g. two
 *     teams can both call their iteration "Sprint 1" but have separate Sprint
 *     records, each referenced from their own workstream's entries).
 */
export interface WorkstreamEntry {
  /** References Sprint.id. */
  sprintId: string;
  /**
   * The pod IDs participating in this sprint on this workstream.
   * Minimum one; multiple pods indicate a shared sprint cadence.
   */
  podIds: string[];
}

/**
 * A workstream groups one or more pods with their sprint schedule.
 *
 * Workstreams are the unit of visualisation in the UI: each active workstream
 * maps to one sprint board view showing the stories assigned to that
 * workstream's current sprint across all of its pods.
 *
 * Multiple workstreams can have overlapping sprint date ranges — for example,
 * the Storefront + Checkout workstream and the Inventory workstream may both
 * be running "Sprint 1" simultaneously but as entirely separate sprint records.
 */
export interface Workstream {
  /** Unique identifier for this workstream. */
  id: string;
  /** Human-readable workstream name (e.g. "Storefront & Checkout"). */
  name: string;
  /** Optional description of the workstream's scope or purpose. */
  description?: string;
  /**
   * The sprint + pod assignments for this workstream, ordered chronologically.
   * Each entry represents one sprint iteration and the pods working it.
   */
  entries: WorkstreamEntry[];
  /**
   * Ordered swim-lane states for this workstream.  Each state represents a
   * column (or lane) that stories can be placed in.  Defaults to
   * DEFAULT_WORKSTREAM_STATES when not otherwise configured.
   */
  states: WorkstreamState[];
  /** ISO date string of when this workstream was created. */
  createdAt: string;
  /** ISO date string of the last time this workstream record was modified. */
  updatedAt?: string;
}

// ---------------------------------------------------------------------------
// Redux slice
// ---------------------------------------------------------------------------

interface WorkstreamsState {
  workstreams: Workstream[];
}

const initialState: WorkstreamsState = {
  workstreams: [],
};

const workstreamsSlice = createSlice({
  name: 'workstreams',
  initialState,
  reducers: {
    /** Replace all workstreams (used for demo data loading and reset). */
    setWorkstreams: (state, action: PayloadAction<Workstream[]>) => {
      state.workstreams = action.payload;
    },
    /** Add a new workstream. */
    addWorkstream: (state, action: PayloadAction<Workstream>) => {
      state.workstreams.push(action.payload);
    },
    /** Update an existing workstream by id. */
    updateWorkstream: (state, action: PayloadAction<Workstream>) => {
      const idx = state.workstreams.findIndex((w) => w.id === action.payload.id);
      if (idx !== -1) state.workstreams[idx] = action.payload;
    },
    /** Delete a workstream by id. */
    deleteWorkstream: (state, action: PayloadAction<string>) => {
      state.workstreams = state.workstreams.filter((w) => w.id !== action.payload);
    },
    /** Replace the ordered swim-lane states for a single workstream. */
    setWorkstreamStates: (
      state,
      action: PayloadAction<{ workstreamId: string; states: WorkstreamState[] }>,
    ) => {
      const ws = state.workstreams.find((w) => w.id === action.payload.workstreamId);
      if (ws) ws.states = action.payload.states;
    },
    /** Add a swim-lane state to a workstream. */
    addWorkstreamState: (
      state,
      action: PayloadAction<{ workstreamId: string; workstreamState: WorkstreamState }>,
    ) => {
      const ws = state.workstreams.find((w) => w.id === action.payload.workstreamId);
      if (ws) ws.states.push(action.payload.workstreamState);
    },
    /** Update an existing swim-lane state on a workstream. */
    updateWorkstreamState: (
      state,
      action: PayloadAction<{ workstreamId: string; workstreamState: WorkstreamState }>,
    ) => {
      const ws = state.workstreams.find((w) => w.id === action.payload.workstreamId);
      if (!ws) return;
      const idx = ws.states.findIndex((s) => s.id === action.payload.workstreamState.id);
      if (idx !== -1) ws.states[idx] = action.payload.workstreamState;
    },
    /** Remove a swim-lane state from a workstream by state id. */
    removeWorkstreamState: (
      state,
      action: PayloadAction<{ workstreamId: string; stateId: string }>,
    ) => {
      const ws = state.workstreams.find((w) => w.id === action.payload.workstreamId);
      if (ws) ws.states = ws.states.filter((s) => s.id !== action.payload.stateId);
    },
  },
});

export const {
  setWorkstreams,
  addWorkstream,
  updateWorkstream,
  deleteWorkstream,
  setWorkstreamStates,
  addWorkstreamState,
  updateWorkstreamState,
  removeWorkstreamState,
} = workstreamsSlice.actions;

export default workstreamsSlice.reducer;
